import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints, formatApiError } from "../lib/api";
import { ACCOUNT_TYPES, ACCOUNT_TYPE_META, CONTACT_ROLES, typeMeta } from "../lib/accountTypes";
import { IdentificationCard, X, CircleNotch, Camera, WarningCircle, CheckCircle, Buildings, Plus, ArrowCounterClockwise } from "@phosphor-icons/react";

// Downscale in the browser so phone photos upload fast (cards stay legible at 1600px).
async function fileToJpegBase64(file, max = 1600) {
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
        const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        return { b64: dataUrl.split(",")[1], preview: dataUrl };
    } finally { URL.revokeObjectURL(url); }
}

export function CardScanButton({ account, onDone, small, label = "Scan card" }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button className={small ? "btn btn-ghost btn-sm" : "btn btn-ghost"} onClick={() => setOpen(true)} data-testid="card-scan-open-btn">
                <IdentificationCard size={small ? 12 : 14} weight="bold" /> {label}
            </button>
            {open && <CardScanModal account={account} onClose={(res) => { setOpen(false); if (res) onDone?.(res); }} />}
        </>
    );
}

export default function CardScanModal({ account, onClose }) {
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [step, setStep] = useState("pick"); // pick | reading | review | saving
    const [preview, setPreview] = useState("");
    const [err, setErr] = useState("");
    const [scan, setScan] = useState(null);
    const [person, setPerson] = useState({ name: "", title: "", email: "", phone: "", role: "" });
    const [company, setCompany] = useState({ name: "", website: "", address: "", phone: "", company_type: "customer" });
    const [target, setTarget] = useState(account?.id || "new"); // account id or "new"

    async function onFile(e) {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setErr(""); setStep("reading");
        try {
            const { b64, preview } = await fileToJpegBase64(f);
            setPreview(preview);
            const { data } = await endpoints.scanCard({ image_base64: b64 });
            setScan(data);
            const p = data.person;
            setPerson({ name: p.name, title: p.title, email: p.email, phone: p.mobile || p.phone, role: p.role });
            setCompany({ ...data.company, phone: data.company.phone || (p.mobile && p.phone ? p.phone : "") });
            if (!account) {
                const strong = data.account_matches.find((m) => m.strength === "strong") || data.account_matches.find((m) => m.reasons.some((r) => r.startsWith("Same email")));
                setTarget(strong ? strong.id : "new");
            }
            setStep("review");
        } catch (e2) { setErr(formatApiError(e2)); setStep("pick"); }
    }

    async function save() {
        if (!person.name.trim() && target !== "new") { setErr("The contact needs a name"); return; }
        setErr(""); setStep("saving");
        try {
            let accountId = target;
            let created = false;
            if (target === "new") {
                if (!company.name.trim()) { setErr("Give the new account a company name"); setStep("review"); return; }
                accountId = crypto.randomUUID();
                await endpoints.saveClient({
                    id: accountId, name: company.name.trim(), type: company.company_type || "customer",
                    website: company.website, address: company.address, phone: company.phone, email: "",
                    contact_name: "", stage: "lead",
                });
                created = true;
            }
            if (person.name.trim()) {
                const body = { name: person.name.trim(), title: person.title, email: person.email, phone: person.phone, role: person.role, is_primary: created };
                try { await endpoints.addClientContact(accountId, body); }
                catch (e) {
                    const d = e?.response?.data?.detail;
                    if (e?.response?.status === 409 && d?.matches) {
                        if (!window.confirm(`${d.message}.\n\nAdd them here as well?`)) { setStep("review"); return; }
                        await endpoints.addClientContact(accountId, { ...body, force: true });
                    } else throw e;
                }
                await endpoints.addClientActivity(accountId, { type: "system", subject: `Contact added from business card: ${person.name.trim()}` }).catch(() => {});
            }
            onClose({ accountId, created });
            if (created || accountId !== account?.id) navigate(`/crm/accounts/${accountId}`);
        } catch (e) { setErr(formatApiError(e)); setStep("review"); }
    }

    const sp = (k) => (e) => setPerson((p) => ({ ...p, [k]: e.target.value }));
    const sc = (k) => (e) => setCompany((c) => ({ ...c, [k]: e.target.value }));

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" data-testid="card-scan-modal">
            <div className="rom-card w-full max-w-xl max-h-[94vh] flex flex-col">
                <div className="rom-card-head shrink-0">
                    <h3 className="flex items-center gap-1.5"><IdentificationCard size={15} weight="bold" /> Scan business card{account ? ` · ${account.name}` : ""}</h3>
                    {step !== "reading" && step !== "saving" && <button onClick={() => onClose(null)}><X size={18} /></button>}
                </div>
                <div className="rom-card-body overflow-y-auto space-y-4">
                    {err && <div className="text-sm text-red-600 flex items-center gap-1.5" data-testid="card-scan-error"><WarningCircle size={14} weight="bold" /> {err}</div>}
                    <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="card-scan-file" />

                    {(step === "pick" || step === "reading") && (
                        <button onClick={() => fileRef.current?.click()} disabled={step === "reading"} data-testid="card-scan-pick"
                            className="w-full rounded-[10px] border-2 border-dashed border-[var(--rom-border-strong)] bg-slate-50/60 py-10 flex flex-col items-center gap-2 hover:border-[var(--rom-navy)]">
                            {step === "reading" ? (
                                <>{preview && <img src={preview} alt="" className="max-h-40 rounded shadow mb-2" />}<CircleNotch size={26} className="animate-spin text-[var(--rom-navy)]" /><span className="text-sm font-semibold">Reading the card…</span></>
                            ) : (
                                <><Camera size={30} weight="bold" className="text-[var(--rom-navy)]" /><span className="text-sm font-semibold">Take a photo or choose an image</span><span className="text-[11px] text-[var(--rom-text-3)]">Card flat, filling the frame, good light</span></>
                            )}
                        </button>
                    )}

                    {(step === "review" || step === "saving") && scan && (
                        <>
                            <div className="flex gap-3 items-start">
                                {preview && <img src={preview} alt="Card" className="w-32 rounded border border-[var(--rom-border)] shrink-0" />}
                                <div className="flex-1 grid grid-cols-2 gap-2">
                                    <Field label="Name" value={person.name} onChange={sp("name")} testId="card-person-name" span />
                                    <Field label="Title" value={person.title} onChange={sp("title")} />
                                    <div><label className="label">Role</label>
                                        <select className="input" value={person.role} onChange={sp("role")} data-testid="card-person-role">
                                            {CONTACT_ROLES.map(([v, l]) => <option key={v} value={v}>{v ? l : "—"}</option>)}
                                        </select>
                                    </div>
                                    <Field label="Email" value={person.email} onChange={sp("email")} testId="card-person-email" />
                                    <Field label="Phone" value={person.phone} onChange={sp("phone")} />
                                </div>
                            </div>
                            {scan.person.mobile && scan.person.phone && scan.person.mobile !== scan.person.phone && (
                                <p className="text-[11px] text-[var(--rom-text-3)] -mt-2">Card also shows office line {scan.person.phone}{target === "new" ? " — used as the new account's main phone" : ""}.</p>
                            )}
                            {scan.contact_matches.length > 0 && (
                                <div className="text-xs rounded-[6px] border border-amber-300 bg-amber-50 p-2.5 flex gap-2" data-testid="card-contact-match">
                                    <WarningCircle size={14} weight="bold" className="text-amber-600 shrink-0 mt-0.5" />
                                    <span>{scan.contact_matches[0].contact_name} with this email/phone is already on <b>{scan.contact_matches[0].client_name}</b>.</span>
                                </div>
                            )}

                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-bold mb-1.5">Add to account</div>
                                <div className="space-y-1.5">
                                    {account && <Choice on={target === account.id} onPick={() => setTarget(account.id)} title={account.name} sub="This account" testId="card-target-current" />}
                                    {scan.account_matches.filter((m) => m.id !== account?.id).map((m) => (
                                        <Choice key={m.id} on={target === m.id} onPick={() => setTarget(m.id)} testId={`card-target-${m.id}`}
                                            title={<>{m.name}{m.parent_name && <span className="font-normal text-[var(--rom-text-3)]"> · under {m.parent_name}</span>}</>}
                                            sub={`${typeMeta(m.type).label} · ${m.reasons.join(" · ")}`} badge={m.strength === "strong" ? "Likely match" : null} />
                                    ))}
                                    <Choice on={target === "new"} onPick={() => setTarget("new")} icon={Plus} title={`New account${company.name ? `: ${company.name}` : ""}`} sub="Create the company as a new lead" testId="card-target-new" />
                                </div>
                            </div>

                            {target === "new" && (
                                <div className="grid grid-cols-2 gap-2 p-3 rounded-[6px] border border-dashed border-[var(--rom-border-strong)]" data-testid="card-new-account">
                                    <Field label="Company" value={company.name} onChange={sc("name")} testId="card-company-name" span />
                                    <div><label className="label">Type</label>
                                        <select className="input" value={company.company_type} onChange={sc("company_type")}>
                                            {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{ACCOUNT_TYPE_META[t].label}</option>)}
                                        </select>
                                    </div>
                                    <Field label="Website" value={company.website} onChange={sc("website")} />
                                    <Field label="Address" value={company.address} onChange={sc("address")} span />
                                    <Field label="Main phone" value={company.phone} onChange={sc("phone")} />
                                </div>
                            )}
                            {scan.notes && <p className="text-[11px] text-[var(--rom-text-3)]">Also on the card: {scan.notes}</p>}

                            <div className="flex items-center justify-between gap-2">
                                <button className="btn btn-ghost" onClick={() => { setStep("pick"); setScan(null); setPreview(""); }} disabled={step === "saving"}><ArrowCounterClockwise size={14} /> Rescan</button>
                                <button className="btn btn-primary" onClick={save} disabled={step === "saving"} data-testid="card-save-btn">
                                    {step === "saving" ? <CircleNotch size={14} className="animate-spin" /> : <><CheckCircle size={14} weight="bold" /> Save contact</>}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, span, testId }) {
    return (
        <div className={span ? "col-span-2" : ""}>
            <label className="label">{label}</label>
            <input className="input" value={value || ""} onChange={onChange} data-testid={testId} />
        </div>
    );
}

function Choice({ on, onPick, title, sub, badge, icon: Icon = Buildings, testId }) {
    return (
        <button type="button" onClick={onPick} data-testid={testId}
            className={`w-full text-left p-2.5 rounded-[6px] border flex items-center gap-2.5 ${on ? "border-[var(--rom-navy)] bg-white ring-1 ring-[var(--rom-navy)]" : "border-[var(--rom-border)] bg-slate-50 hover:border-[var(--rom-border-strong)]"}`}>
            <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${on ? "border-[var(--rom-navy)] bg-[var(--rom-navy)] shadow-[inset_0_0_0_2px_white]" : "border-[var(--rom-border-strong)]"}`} />
            <Icon size={14} weight="bold" className="text-[var(--rom-text-3)] shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{title}</div>
                {sub && <div className="text-[11px] text-[var(--rom-text-3)] truncate">{sub}</div>}
            </div>
            {badge && <span className="chip chip-orange text-[10px] shrink-0">{badge}</span>}
        </button>
    );
}
